import React from 'react'
import { Table, TableCol, TableRow } from '../../../../../components/pdf/Table'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import {
  getAverageByType,
  getProtectionDegreeAverage,
  getGRLevelLabel,
  getMoslerList,
  getPrivateRiskLevelLabel,
  getPrivateRiskLevelPriority,
  sumGrauVulnerabilidadeByCategoryId,
  sumValueByCategoryId
} from '../../_helpers/pdfHelpers'
import Separator from '../../../../../components/helpers/Separator'
import Header from '../../_shared/Header'
import BarChart from '../../components/chats/BarChart'
import { Col } from '../../../../../components/pdf/Grid'

const ChassiPlataforma = ({ headerValues, moslerValues, diagSegValues }) => {
  const riskList = getMoslerList(moslerValues)

  headerValues = {
    tipo: 'CHASSI DO DIAGNÓSTICO DE SEGURANÇA',
    titulo: '',
    ...headerValues
  }

  const andaresInternos = diagSegValues.find(item => item.type === 'andaresInternos')
  const recepcoes = diagSegValues.find(item => item.type === 'recepcoes')
  const hallElevSS = diagSegValues.find(item => item.type === 'hallElevSS')
  const sistemasGerais = diagSegValues.find(item => item.type === 'sistemasGerais')

  const valuesByType = getAverageByType(diagSegValues)

  const grauVulnerabilidade = valuesByType.find(item => item.type === 'andaresInternos')?.grauVulnerabilidade
  const nivelVulnerabilidade = (99.8 - getProtectionDegreeAverage(diagSegValues)).toFixed(2)

  const prevCombateIncendioValue = diagSegValues.find(item => item.type === 'sistemasGerais')?.prevencaoCombateIncendios
    ?.result
  const grauProtecaoSS = diagSegValues.find(item => item.type === 'hallElevSS')?.grauProtecao
  const grauProtecaoRecep = diagSegValues.find(item => item.type === 'recepcoes')?.grauProtecao
  const controlAccessPrivados = diagSegValues.find(item => item.type === 'andaresInternos')?.controlAccess

  const mediaGPeV = (grauProtecaoSS + grauProtecaoRecep + controlAccessPrivados) / 3

  const listaRiscoPotenciais = getMoslerList(moslerValues, prevCombateIncendioValue, mediaGPeV)

  const mediaPCIGR = sumValueByCategoryId(listaRiscoPotenciais, [23, 24, 31, 34])
  const mediaCAGR = sumValueByCategoryId(listaRiscoPotenciais, [25, 33, 26, 27, 28, 29, 30, 32, 34, 35])

  const mediaPCIGV = sumGrauVulnerabilidadeByCategoryId(listaRiscoPotenciais, [23, 24, 31, 34])
  const mediaCAGV = sumGrauVulnerabilidadeByCategoryId(listaRiscoPotenciais, [25, 33, 26, 27, 28, 29, 30, 32, 34, 35])

  const GRChartData = [
    { label: 'GRAU DE RISCO PREVENÇÃO E COMBATE A INCÊNDIO MÉTODO DE MOSLER', value: mediaPCIGR },
    { label: 'GRAU DE RISCO CONTROLE DE ACESSO MÉTODO DE MOSLER', value: mediaCAGR }
  ]

  const GVChartData = [
    {
      label: 'GRAU DE VULNERABILIDADE DO EDIFÍCIO EM PREVENÇÃO E COMBATE A INCÊNDIO',
      value: parseFloat(mediaPCIGV.toFixed(2))
    },
    { label: 'GRAU DE VULNERABILIDADE DO EDIFÍCIO EM CONTROLE DE ACESSO', value: parseFloat(mediaCAGV.toFixed(2)) }
  ]

  const AmbienteInternoChartData = [
    {
      label: 'NÍVEL OBJETIVO DE VULNERABILIDADE',
      value: parseFloat(grauVulnerabilidade)
    }
  ]

  const AmbienteExternoChartData = [
    {
      label: 'NÍVEL OBJETIVO DE VULNERABILIDADE',
      value: parseFloat(nivelVulnerabilidade)
    }
  ]

  const MoslerConfigChart = {
    data: GRChartData,
    height: 300,
    xField: 'label',
    yField: 'value',
    colorField: 'value',
    color: '#9999ff',
    yAxis: {
      max: 1250,
      label: {
        formatter: value => value
      }
    },
    xAxis: {
      label: {
        style: {
          fontSize: 7.5,
          textAlign: 'center'
        },
        autoEllipsis: false,
        offset: 10,
        rotate: false
      }
    },
    label: {
      position: 'top',
      formatter: ({ value }) => value
    }
  }

  const PercentConfigChart = {
    height: 300,
    xField: 'label',
    yField: 'value',
    colorField: 'value',
    color: '#9999ff',
    yAxis: {
      max: 100,
      label: {
        formatter: value => value
      }
    },
    xAxis: {
      label: {
        style: {
          fontSize: 7.5,
          textAlign: 'center'
        },
        autoEllipsis: false,
        offset: 10,
        rotate: false
      }
    },
    label: {
      position: 'top',
      formatter: ({ value }) => value
    }
  }

  const GVConfigChart = {
    data: GVChartData,
    ...PercentConfigChart
  }

  const AmbienteInternoConfigChart = {
    data: AmbienteInternoChartData,
    ...PercentConfigChart
  }

  const AmbienteExternoConfigChart = {
    data: AmbienteExternoChartData,
    ...PercentConfigChart
  }

  return (
    <View pageBreakAfter>
      <Header
        headerValues={headerValues}
        category="INDICADORES DE GRAUS DE RISCOS E VULNERABILIDADES DAS ÁREA PRIVATIVAS ITAÚ E EDIFÍCIOS CONDOMÍNIOS"
      />
      <Table width="100%" useFontBaseColor style={{ marginTop: '-1px' }}>
        <TableRow bgColor="#f2dbdb" useFontBaseColor>
          <TableCol useFontBaseColor colSpan={2}>
            <Text semibold size={10} align="center">
              LEGISLAÇÃO E NORMAS
            </Text>
          </TableCol>
          <TableCol useFontBaseColor colSpan={2}>
            <Text semibold size={10} align="center" preLine>
              {
                'GR\nGRAU DE RISCO - MÉDIA UTILIZANDO MÉTODO DE MOSLER PARA OS RISCOS IDENTIFICADOS RELATIVOS A INCÊNDIO E CONTROLE DE ACESSO'
              }
            </Text>
          </TableCol>
          <TableCol useFontBaseColor colSpan={2}>
            <Text semibold size={10} align="center" preLine>
              {'GV\nÍNDICE LEVANTADO NA EDIFICAÇÃO'}
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor colSpan={2} rowSpan={3}>
            <Text size={10} align="center">
              Lei nº 14.967 de 09 de setembro de 2024 - Estatuto da Segurança Privada Código de Incêndio -Decreto
              Estadual 63.911/18 IT 16 – Gerenciamento de Riscos de Incêndio IT 17 – Brigada de Incêndio NBR ISO
              31.000/2018 – Gestão de riscos – Diretrizes NBR ISO 31.010/2019 – Técnicas de Avaliação de Riscos ABNT,
              NBR 9050/2020 - Acessibilidade NR-23
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              GRAU DE RISCO PREVENÇÃO E COMBATE A INCÊNDIO MÉTODO DE MOSLER
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              GRAU DE RISCO CONTROLE DE ACESSO MÉTODO DE MOSLER
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              GRAU DE VULNERABILIDADE DO EDIFÍCIO EM PREVENÇÃO E COMBATE A INCÊNDIO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              GRAU DE VULNERABILIDADE DO EDIFÍCIO EM CONTROLE DE ACESSO
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {mediaPCIGR.toFixed(2)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {mediaCAGR.toFixed(2)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {mediaPCIGV.toFixed(2)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {mediaCAGV.toFixed(2)}
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getGRLevelLabel(mediaPCIGR)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getGRLevelLabel(mediaCAGR)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getPrivateRiskLevelLabel(mediaPCIGV)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getPrivateRiskLevelLabel(mediaCAGV)}
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor colSpan={6}>
            <Col gutter="1rem 2rem">
              <Text size={10} align="center" preLine>
                {'GRAUS DE RISCO\nPREVENÇÃO E COMBATE A INCÊNDIO / CONTROLE DE ACESSO\nMÉTODO DE MOSLER'}
              </Text>
              <Separator weight={5} />
              {!!GRChartData.length && <BarChart {...MoslerConfigChart} />}
            </Col>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor colSpan={6}>
            <Col gutter="1rem 2rem">
              <Text size={10} align="center" preLine>
                {
                  'GRAU DE VULNERABILIDADE\nPREVENÇÃO E COMBATE A INCÊNDIO / CONTROLE DE ACESSO\nESTRUTURA DA EDIFICAÇÃO'
                }
              </Text>
              <Separator weight={5} />
              {!!GVChartData.length && <BarChart {...GVConfigChart} />}
            </Col>
          </TableCol>
        </TableRow>
      </Table>

      <Separator weight={10} />

      <Table width="100%" useFontBaseColor>
        <TableRow useFontBaseColor>
          <TableCol bgColor="#f2dbdb" useFontBaseColor colSpan={6}>
            <Text bold size={12} align="center">
              AMBIENTES
            </Text>
          </TableCol>
        </TableRow>
        <TableRow bgColor="#f2dbdb" useFontBaseColor>
          <TableCol useFontBaseColor colSpan={3} width="50%">
            <Text semibold size={10} align="center" preLine>
              {'INTERNO\nÁREA PRIVATIVA'}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor colSpan={3} width="50%">
            <Text semibold size={10} align="center" preLine>
              {'EXTERNO\nCONDOMÍNIO'}
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor width="15%">
            <Text size={10} align="center">
              NÍVEL OBJETIVO DE VULNERABILIDADE
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="20%">
            <Text size={10} align="center">
              NÍVEL DE RISCO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="15%">
            <Text size={10} align="center">
              PRIORIDADE
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="15%">
            <Text size={10} align="center">
              NÍVEL OBJETIVO DE VULNERABILIDADE
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="20%">
            <Text size={10} align="center">
              NÍVEL DE RISCO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="15%">
            <Text size={10} align="center">
              PRIORIDADE
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {grauVulnerabilidade}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getPrivateRiskLevelLabel(grauVulnerabilidade)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getPrivateRiskLevelPriority(grauVulnerabilidade)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {nivelVulnerabilidade}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getPrivateRiskLevelLabel(nivelVulnerabilidade)}
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text semibold size={10} align="center">
              {getPrivateRiskLevelPriority(nivelVulnerabilidade)}
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor colSpan={3}>
            <Col gutter="2rem 1rem">
              <Text size={10} align="center" preLine>
                {'AMBIENTE INTERNO\nÁREA PRIVATIVA'}
              </Text>
              <Separator weight={5} />
              {!!AmbienteInternoChartData.length && <BarChart {...AmbienteInternoConfigChart} />}
            </Col>
          </TableCol>
          <TableCol useFontBaseColor colSpan={3}>
            <Col gutter="2rem 1rem">
              <Text size={10} align="center" preLine>
                {'AMBIENTE EXTERNO\nCONDOMÍNIO/EDIFÍCIO'}
              </Text>
              <Separator weight={5} />
              {!!AmbienteExternoChartData.length && <BarChart {...AmbienteExternoConfigChart} />}
            </Col>
          </TableCol>
        </TableRow>
      </Table>

      <Separator weight={10} />

      <Table width="100%" useFontBaseColor>
        <TableRow useFontBaseColor>
          <TableCol bgColor="#f2dbdb" useFontBaseColor colSpan={6}>
            <Text bold size={12} align="center">
              PILAR DE CLASSIFICAÇÃO
            </Text>
          </TableCol>
        </TableRow>
        <TableRow bgColor="#f2dbdb" useFontBaseColor>
          <TableCol useFontBaseColor colSpan={2}>
            <Text semibold size={10} align="center" preLine>
              Perímetro Interno (Dentro ambiente IBBA)
            </Text>
          </TableCol>
          <TableCol useFontBaseColor colSpan={2}>
            <Text semibold size={10} align="center" preLine>
              Perímetro Externo (Ambiente condomínio)
            </Text>
          </TableCol>
        </TableRow>
        <TableRow bgColor="#f2dbdb" useFontBaseColor>
          <TableCol useFontBaseColor width="16,66%">
            <Text semibold size={10} align="center">
              ITENS AVALIADOS APENAS NA ÁREA PRIVATIVA
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="16,66%">
            <Text semibold size={10} align="center">
              ÍNDICE DE PROTEÇÃO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="16,66%">
            <Text semibold size={10} align="center">
              ITENS AVALIADOS NO CONDOMÍNIO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="16,66%">
            <Text semibold size={10} align="center">
              ÍNDICE DE PROTEÇÃO
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              ACESSO CONTROLADO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.controlAccess.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              ACESSO CONTROLADO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {((recepcoes.controlAccess + hallElevSS.controlAccess) / 2).toFixed(2)}%
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              CFTV
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.circuitTV.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              CFTV
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {((recepcoes.circuitTV + hallElevSS.circuitTV) / 2).toFixed(2)}%
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              INFRAESTRUTURA
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.infra.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              INFRAESTRUTURA
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {((recepcoes.infra + hallElevSS.infra + sistemasGerais.infra) / 3).toFixed(2)}%
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              ATENDIMENTO A EMERGÊNCIA
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.medicalEmergency.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              ATENDIMENTO A EMERGÊNCIA
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {(
                (recepcoes.medicalEmergency + hallElevSS.medicalEmergency + sistemasGerais.medicalEmergency) /
                3
              ).toFixed(2)}
              %
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              SISTEMAS DE INCÊNDIO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.fireSystem.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              SISTEMAS DE INCÊNDIO
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {((recepcoes.fireSystem + sistemasGerais.fireSystem) / 2).toFixed(2)}%
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              DEMANDA LEGAL
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.legalDemand.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              DEMANDA LEGAL
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {((recepcoes.legalDemand + sistemasGerais.legalDemand) / 2).toFixed(2)}%
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              PROCESSOS
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.emergencyPlans.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              PROCESSOS
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {((recepcoes.emergencyPlans + sistemasGerais.emergencyPlans) / 2).toFixed(2)}%
            </Text>
          </TableCol>
        </TableRow>
        <TableRow useFontBaseColor>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              PROCEDIMENTOS
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {andaresInternos.procedure.toFixed(2)}%
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              PROCEDIMENTOS
            </Text>
          </TableCol>
          <TableCol useFontBaseColor>
            <Text size={10} align="center">
              {((recepcoes.procedure + sistemasGerais.procedure + hallElevSS.procedure) / 3).toFixed(2)}%
            </Text>
          </TableCol>
        </TableRow>
      </Table>

      <Separator weight={10} />

      <Table width="100%" useFontBaseColor>
        <TableRow useFontBaseColor>
          <TableCol bgColor="#f2dbdb" useFontBaseColor colSpan={3}>
            <Text bold size={12} align="center">
              RISCOS
            </Text>
          </TableCol>
        </TableRow>
        <TableRow bgColor="#f2dbdb" useFontBaseColor>
          <TableCol useFontBaseColor width="50%">
            <Text semibold size={10} align="center" preLine>
              AVALIADOS PELO MÉTODO DE MOSLER
            </Text>
          </TableCol>
          <TableCol useFontBaseColor width="10%">
            <Text semibold size={10} align="center" preLine>
              CR
            </Text>
          </TableCol>
        </TableRow>
        {riskList.map(item => {
          return (
            <>
              <TableRow useFontBaseColor>
                <TableCol useFontBaseColor>
                  <Text size={10} align="center" preLine>
                    {item.category}
                  </Text>
                </TableCol>
                <TableCol useFontBaseColor>
                  <Text size={10} align="center">
                    {item.value}
                  </Text>
                </TableCol>
              </TableRow>
            </>
          )
        })}
      </Table>
    </View>
  )
}

export default ChassiPlataforma
